// src/pages/ProfilePage.js
import React, { useEffect, useState, useMemo, useContext, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import './ProfilePage.css';
import { ClientContext } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import api from '../services/api';
import PurchaseFlow from '../components/PurchaseFlow/PurchaseFlow';
import CurrentAccount from '../components/Profile/CurrentAccount';
import ClientProfile from '../components/Profile/ClientProfile';
import LoanRefinancing from '../components/Profile/LoanRefinancing';
import ProofDocuments from '../components/Profile/ProofDocuments';
import PaymentFlow from '../components/PaymentFlow/PaymentFlow';
import ClientSales from '../components/Profile/ClientSales';
import UnifiedAuthorizationModal from '../components/PurchaseFlow/UnifiedAuthorizationModal';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import HomonymModal from '../components/Modals/HomonymModal';
import NewClientModal from '../components/Modals/NewClientModal';
import BankSelectionModal from '../components/Modals/BankSelectionModal';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { formatNumber, formatCurrency } from '../utils/numberFormatter';
import { formatDNI } from '../utils/documentFormatter';
import { handleValidationErrors } from '../utils/errorHandler';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping, faTags, faArrowsRotate, faFileLines, faCirclePlus, faChevronRight, faFolderOpen, faUser } from '@fortawesome/free-solid-svg-icons';
import { Modal } from 'react-bootstrap';
import QRCode from 'qrcode';
import { useProgress } from '../context/ProgressContext';
import { trackClientProfileViewed, trackClientSearched, trackNewClientCreated } from '../analytics/events';
import { useAnalyticsContext } from '../analytics/useAnalyticsContext';

const MySwal = withReactContent(Swal);

const ProfilePage = () => {
  const { dni } = useParams();
  const location = useLocation();
  const { clientData, setClientData, clientStatus, setClientStatus, clientAuthorization, setClientAuthorization, clientException, setClientException } = useContext(ClientContext);
  const { channelId, branchId, config, setConfig } = useAuth();
  const { commerceId, branchId: analyticsBranchId } = useAnalyticsContext();
  const [loading, setLoading] = useState(true);
  const [showPurchaseFlow, setShowPurchaseFlow] = useState(false);
  const [showPaymentFlow, setShowPaymentFlow] = useState(false);
  const [activeContent, setActiveContent] = useState(null);
  const [homonyms, setHomonyms] = useState([]);
  const [showHomonymModal, setShowHomonymModal] = useState(false);
  
  const [showUnifiedAuthorizationModal, setShowUnifiedAuthorizationModal] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [showBankSelectionModal, setShowBankSelectionModal] = useState(false);
  const [selectedNewClient, setSelectedNewClient] = useState(null);
  const [clientWithBank, setClientWithBank] = useState(null);

  const [canRetryCredit, setCanRetryCredit] = useState(true);
  const [retryCountdown, setRetryCountdown] = useState(0);

  const [isRequesting, setIsRequesting] = useState(false);
  const abortControllerRef = useRef(null);
  const [isMounted, setIsMounted] = useState(true);

  const [showShareModal, setShowShareModal] = useState(false);
  const { progressText, setProgressText, isLoading, reloadProgress } = useProgress();

  // Función para obtener la acción del estado de navegación
  const getActionFromLocationState = () => {
    return location.state?.pendingAction;
  };

  // Función para manejar la acción automática
  const handleAutomaticAction = (action) => {
    if (!action || !clientData) return;

    // Limpiar el estado de navegación para evitar ejecuciones múltiples
    window.history.replaceState({}, document.title);

    switch (action) {
      case 'sale':
        if (permissions.canPurchase) {
          startPurchaseFlow();
        } else {
          MySwal.fire({
            icon: 'warning',
            title: 'No disponible',
            text: 'Este cliente no puede realizar compras en este momento.',
          });
        }
        break;
      case 'collection':
        if (permissions.canMakePayments) {
          startPaymentFlow();
        } else {
          MySwal.fire({
            icon: 'warning',
            title: 'No disponible',
            text: 'Este cliente no puede realizar pagos en este momento.',
          });
        }
        break;
      default:
        break;
    }
  };

  const handleRequestAuthorization = async (payload) => {
    try {
      // Crear FormData para enviar archivo adjunto
      const formData = new FormData();
      formData.append('commerce_id', channelId);
      formData.append('channel_branch_id', branchId);
      formData.append('amount', payload.amount);
      formData.append('observation', payload.observation);
      
      if (payload.password) {
        formData.append('password', payload.password);
      }
      
      if (payload.attachment) {
        formData.append('attachment', payload.attachment);
      }

      const response = await api.post(`/customers/${clientData.id}/authorizations`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
  
      if (response.status === 201) {
        setClientAuthorization(response.data.authorization);
        setShowUnifiedAuthorizationModal(false);
        
        // Limpiar cualquier acción pendiente para evitar que se ejecute automáticamente
        window.history.replaceState({}, document.title);
        
        MySwal.fire({
          title: 'Operación exitosa',
          text: response.data.message,
          icon: response.data.authorization.status === 'approved' ? 'success' : 'info',
        });

        if(response.data.authorization.status === 'approved') {
          setProgressText('Autorizado');
          // Actualizar límites inmediatamente después de aprobación
          if (isMounted) {
            const creditInfo = await fetchCreditInfo(clientData.id);
            if (creditInfo && isMounted) {
              const effectiveLimits = getEffectiveLimits(creditInfo.limits);
              setClientData(prevData => ({
                ...prevData,
                total_limit: effectiveLimits.total,
                available_credit: effectiveLimits.available,
              }));
            }
          }
        }
        
        // Return response data so modal can track authorization status
        return response.data;
      }

    } catch (error) {
      console.error('Error al solicitar autorización:', error);
      const errorConfig = handleValidationErrors(error);
      MySwal.fire(errorConfig);
      throw error; // Re-lanzar el error para que el modal pueda resetear el estado de loading
    }
  };

  const handleRequestOverrideException = async (payload) => {
    try {
      const requestData = {
        override_exception_type_id: payload.tipo,
        requested_amount: payload.amount,
        preferred_installments: payload.cuotas,
        seller_comment: payload.observaciones,
      };

      const response = await api.post(`/customers/${clientData.id}/override-exceptions`, requestData);
  
      if (response.status === 201) {
        setClientException(response.data.override_exception);
        setShowUnifiedAuthorizationModal(false);
        
        // Limpiar cualquier acción pendiente para evitar que se ejecute automáticamente
        window.history.replaceState({}, document.title);
        
        MySwal.fire({
          title: 'Operación exitosa',
          text: response.data.message || 'La excepción ha sido solicitada correctamente. Será revisada por un administrador.',
          icon: 'info',
        });

        // Si la excepción fue aprobada automáticamente, actualizar límites
        if(response.data.override_exception.status === 'approved') {
          setProgressText('Autorizado');
          if (isMounted) {
            const creditInfo = await fetchCreditInfo(clientData.id);
            if (creditInfo && isMounted) {
              const effectiveLimits = getEffectiveLimits(creditInfo.limits);
              setClientData(prevData => ({
                ...prevData,
                total_limit: effectiveLimits.total,
                available_credit: effectiveLimits.available,
              }));
            }
          }
        }
      }

    } catch (error) {
      console.error('Error al solicitar excepción:', error);
      const errorConfig = handleValidationErrors(error);
      MySwal.fire(errorConfig);
      throw error; // Re-lanzar el error para que el modal pueda resetear el estado de loading
    }
  };

  const fetchCustomerData = async (dni) => {
    try {
      const response = await api.get(`/customers/search?document=${dni}`);
      return response.data.results;
    } catch (error) {
      console.error('Error al buscar cliente en el backoffice:', error);
      return [];
    }
  };

  function splitFullName(fullName) {
    const words = fullName.trim().toLowerCase().split(' ');
  
    let firstName = '';
    let lastName = '';
  
    if (words.length === 1) {
      firstName = words[0];
    } else if (words.length === 2) {
      lastName = words[0];
      firstName = words[1];
    } else if (words.length === 3) {
      lastName = words[0];
      firstName = words.slice(1).join(' ');
    } else if (words.length >= 4) {
      lastName = words.slice(0, 2).join(' ');
      firstName = words.slice(2).join(' ');
    }

    return {
      firstName: toProperCase(firstName),
      lastName: toProperCase(lastName),
    };
  }
  
  function toProperCase(text) {
    return text
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }  

  /**
   * Helper function para obtener los límites correctos basados en autorización/excepción activa
   * 
   * La estructura de límites del backend es:
   * - total_limit: Límite original del cliente
   * - available_limit: Disponible del límite original (considerando consumos previos)
   * - available_authorized: Límite extendido cuando hay autorización/excepción activa
   * - authorization_id: ID de la autorización activa (null si no hay)
   * 
   * Lógica:
   * - Con autorización/excepción: Mostramos available_authorized como límite total y disponible
   * - Sin autorización/excepción: Mostramos total_limit y available_limit normales
   * 
   * Nota: Las autorizaciones/excepciones son de un solo uso y extienden el límite temporalmente
   */
  const getEffectiveLimits = (limits) => {
    if (!limits) return { total: 0, available: 0 };
    
    // Si hay autorización activa (authorization_id presente), usar available_authorized
    if (limits.authorization_id) {
      return {
        total: limits.available_authorized || limits.total_limit,
        available: limits.available_authorized || limits.available_limit
      };
    }
    
    // Sin autorización, usar los límites normales
    return {
      total: limits.total_limit,
      available: limits.available_limit
    };
  };

  const handleNewClient = async (client) => {
    setLoading(true);
    try {

      const requestData = {
        cuil_cuit: client.cuit,
        name: client.name,
        birthdate: client.birthdate,
        gender: client.gender,
        bank_id: client.bank?.id || null,
      };

      const response = await api.post('/customers', requestData);
  
      if (response.status === 201) {
        const { id } = response.data;
        const { firstName, lastName } = splitFullName(client.name);
  
        // Track new client created
        if (commerceId) {
          trackNewClientCreated({
            commerceId,
            branchId: analyticsBranchId,
            customerDni: client.dni,
            customerName: client.name,
            customerGender: client.gender
          });
        }

        const newClientData = {
          id,
          dni: client.dni,
          cuit: client.cuit,
          firstName,
          lastName,
          birthDate: client.birthdate,
          gender: client.gender,
          type: client.type,
          status: 'PENDING_EVALUATION',
          total_limit: 0,
          available_credit: 0
        };
  
        setClientData(newClientData);
        setClientStatus(client.type);
        setShowNewClientModal(false);
        setShowBankSelectionModal(false);
        setShowHomonymModal(false);
        setLoading(false);
  
        MySwal.fire({
          title: 'Cliente creado',
          text: 'El cliente ha sido creado correctamente.',
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
  
        if (isMounted) {
          const creditInfo = await fetchCreditInfo(id);
          if (creditInfo && isMounted) {
            const effectiveLimits = getEffectiveLimits(creditInfo.limits);
            setClientData(prevData => ({
              ...prevData,
              total_limit: effectiveLimits.total,
              available_credit: effectiveLimits.available,
              status: creditInfo.status,
              client_type: creditInfo.client_type,
              risk_level: creditInfo.risk_level,
              employment_status: creditInfo.employment_status
            }));
          }
        }
      }
    } catch (error) {
      if (!isMounted) return;
      
      console.error('Error al crear el cliente:', error);
      MySwal.fire({
        title: 'Error al crear el cliente',
        text: 'Hubo un problema al crear el cliente. Por favor, intenta nuevamente.',
        icon: 'error',
      });
      setLoading(false);
    }
    };

  // Función para manejar cuando el usuario confirma el cliente nuevo desde NewClientModal
  const handleNewClientConfirm = (client) => {
    // Si está configurado para validar banco, mostrar modal de selección de banco
    if (process.env.REACT_APP_VALIDATE_BANK === 'true') {
      setSelectedNewClient(client);
      setShowNewClientModal(false);
      setShowHomonymModal(false); // Ocultar también el modal de homónimos
      setShowBankSelectionModal(true);
    } else {
      // Si no requiere banco, proceder directamente
      handleNewClient(client);
    }
  };

  // Función para manejar cuando el usuario selecciona el banco
  const handleBankSelection = (clientWithBank) => {
    setClientWithBank(clientWithBank);
    setShowBankSelectionModal(false);
    handleNewClient(clientWithBank);
  };

  // Función para cerrar el modal de selección de banco
  const handleCloseBankSelection = () => {
    setShowBankSelectionModal(false);
    setShowNewClientModal(true); // Volver al modal anterior
    // No mostrar el modal de homónimos ya que ya se seleccionó un cliente
  };
  
  const handleExistingClient = async (client) => {
    const firstName = client.firstname || splitFullName(client.name).firstName;
    const lastName = client.lastname || splitFullName(client.name).lastName;
    
    const genderMap = { M: 'M', F: 'F' };
    const gender = genderMap[client.gender] || 'otro';

    const clientDataFromPadron = {
      id: client.id,
      dni: client.dni,
      cuit: client.cuit,
      firstName,
      lastName,
      birthDate: client.birthdate,
      gender,
      type: client.type,
      status: 'PENDING_EVALUATION',
      total_limit: 0,
      available_credit: 0,
      client_type: null,
      risk_level: null,
      employment_status: null,
      my_account_url: client.my_account_url
    };

    setClientData(clientDataFromPadron);
    setClientStatus(client.type);
    setLoading(false);

    if (isMounted) {
      const creditInfo = await fetchCreditInfo(client.id);

      if (creditInfo && isMounted) {
        const effectiveLimits = getEffectiveLimits(creditInfo.limits);
        setClientData(prevData => {
          const newData = {
            ...prevData,
            total_limit: effectiveLimits.total,
            available_credit: effectiveLimits.available,
            status: creditInfo.status,
            client_type: creditInfo.client_type,
            risk_level: creditInfo.risk_level,
            employment_status: creditInfo.employment_status,
            bank: creditInfo.bank
          };
          
          // Track client profile viewed when credit info is loaded
          if (commerceId && newData.dni && newData.available_credit !== undefined) {
            trackClientProfileViewed({
              commerceId,
              branchId: analyticsBranchId,
              customerDni: newData.dni,
              availableCredit: newData.available_credit
            });
          }
          
          return newData;
        });
      }
    }
  };

  const fetchClientProfile = async (dni) => {
    setLoading(true);
    reloadProgress();
    try {
      const customerData = await fetchCustomerData(dni);
  
      if (!isMounted) return;

      // Track client searched
      if (commerceId) {
        trackClientSearched({
          commerceId,
          branchId: analyticsBranchId,
          customerDni: dni,
          found: customerData.length > 0,
          hasHomonyms: customerData.length > 1
        });
      }
  
      if (customerData.length === 0) {
        MySwal.fire({
          title: 'DNI no encontrado',
          text: 'El DNI ingresado no es existente o válido. Por favor, verifica los datos.',
          icon: 'warning',
          confirmButtonText: 'Volver al inicio'
        }).then(() => {
          window.location.href = '/';
        });
        setClientData(null);
        setClientStatus('');
        setClientAuthorization(null);
        setClientException(null);
        setLoading(false);
        return;
      }
  
      if (customerData.length > 1) {
        setHomonyms(customerData);
        setShowHomonymModal(true);
        setLoading(false);
        return;
      }
  
      const customer = customerData[0];
      if (customer.exists) {
        await handleExistingClient(customer);
      } else {
        setSelectedNewClient(customer);
        setShowNewClientModal(true);
        setLoading(false);
      }
    } catch (error) {
      if (!isMounted) return;
      
      console.error('Error al buscar cliente:', error);
      MySwal.fire({
        title: 'Error al buscar cliente',
        text: 'Hubo un problema al realizar la búsqueda. Intenta nuevamente.',
        icon: 'error',
        confirmButtonText: 'Volver al inicio'
      }).then(() => {
        window.location.href = '/';
      });
      setClientData(null);
      setClientStatus('');
      setClientAuthorization(null);
      setClientException(null);
      setLoading(false);
    }
  };

  const startRetryCountdown = () => {
    setCanRetryCredit(false);
    setRetryCountdown(60);
    
    const timer = setInterval(() => {
      setRetryCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanRetryCredit(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleManualCreditCheck = async () => {
    if (!canRetryCredit || !isMounted) return;
    
    try {
      setIsRequesting(true);
      const response = await api.get(`/customers/${clientData.id}/credit-info`, {
        signal: abortControllerRef.current?.signal
      });
      
      if (!isMounted) return;
      
      if (response.data.status === 'PENDING_EVALUATION') {
        setClientData(prevData => ({
          ...prevData,
          status: 'PENDING_EVALUATION'
        }));
      } else {
        const effectiveLimits = getEffectiveLimits(response.data.limits);
        setClientData(prevData => ({
          ...prevData,
          total_limit: effectiveLimits.total,
          available_credit: effectiveLimits.available,
          status: 'COMPLETED',
          client_type: response.data.client_type,
          risk_level: response.data.risk_level,
          employment_status: response.data.employment_status,
          bank: response.data.bank
        }));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error al consultar límite:', error);
      }
    } finally {
      if (isMounted) {
        setIsRequesting(false);
        startRetryCountdown();
      }
    }
  };

  const handleAuthorizationCheck = async () => {
    // TODO: Implementar la consulta de la autorización.
    window.location.reload(); // workaround para que se actualice la info de la autorización.
  };

  const fetchCreditInfo = async (clientId) => {
    const MAX_RETRIES = 5;
    const RETRY_DELAY = 5000;
    let retryCount = 0;

    const attemptFetch = async () => {
      try {
        if (!isMounted) return null;
        
        console.log(`Iniciando intento ${retryCount + 1} de ${MAX_RETRIES}`);
        
        const response = await api.get(`/customers/${clientId}/credit-info`, {
          signal: abortControllerRef.current?.signal
        });
        
        if (!isMounted) return null;
        
        if (response.data.status === 'PENDING_EVALUATION') {
          if (retryCount < MAX_RETRIES) {
            retryCount++;
            console.log(`Esperando ${RETRY_DELAY}ms para el siguiente intento`);
            
            try {
              await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(resolve, RETRY_DELAY);
                abortControllerRef.current?.signal.addEventListener('abort', () => {
                  clearTimeout(timeoutId);
                  reject(new Error('Cancelled'));
                });
              });
              
              if (!isMounted) return null;
              return attemptFetch();
            } catch (error) {
              if (error.message === 'Cancelled') {
                console.log('Consulta cancelada durante la espera');
                return null;
              }
              throw error;
            }
          } else {
            console.log('Máximo de intentos alcanzado');
            startRetryCountdown();
            return {
              limits: {
                total_limit: 0,
                available_limit: 0,
                available_authorized: 0,
                authorization_id: null
              },
              status: 'PENDING_EVALUATION'
            };
          }
        } else if (response.data.status === 'REJECTED') {
          return {
            limits: {
              total_limit: 0,
              available_limit: 0,
              available_authorized: 0,
              authorization_id: null
            },
            status: 'REJECTED',
            message: response.data.message,
            bank: response.data.bank
          };
        } else {
          console.log('Consulta exitosa');
          setProgressText('Disponible');
          // Si hay información de self_management, actualizamos solo esa parte del config
          if (response.data.self_management) {
            setConfig(prevConfig => ({
              ...prevConfig,
              self_authorization: response.data.self_management
            }));

            // También actualizamos en localStorage
            const storedConfig = JSON.parse(localStorage.getItem('config') || '{}');
            localStorage.setItem('config', JSON.stringify({
              ...storedConfig,
              self_authorization: response.data.self_management
            }));
          }

          // Carga autorización o excepción actual
          if(response.data.authorization) {
            setClientAuthorization(response.data.authorization);
            
            if(response.data.authorization.status === 'approved') {
              setProgressText('Autorizado');
            }
          }

          if(response.data.exception) {
            setClientException(response.data.exception);
            
            if(response.data.exception.status === 'approved') {
              setProgressText('Autorizado');
            }
          }

          // Siempre retornar los límites del servicio con la estructura completa
          return {
            limits: response.data.limits, // Devolvemos el objeto completo de límites
            status: 'COMPLETED',
            client_type: response.data.client_type,
            risk_level: response.data.risk_level,
            employment_status: response.data.employment_status,
            bank: response.data.bank
          };
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.message === 'Cancelled') {
          console.log('Consulta de crédito cancelada');
          return null;
        }
        if (error.response?.status === 403) {
          return {
            limits: {
              total_limit: 0,
              available_limit: 0,
              available_authorized: 0,
              authorization_id: null
            },
            status: 'REJECTED',
            message: error.response.data.message || 'No hay crédito disponible.',
            bank: error.response.data.bank || null
          };
        }
        console.error('Error al obtener información de crédito:', error);
        return null;
      }
    };

    setIsRequesting(true);
    const result = await attemptFetch();
    
    // Solo desactivamos isRequesting si:
    // 1. El componente sigue montado
    // 2. Obtuvimos un resultado exitoso (status COMPLETED) o alcanzamos el máximo de intentos
    if (isMounted && (result?.status === 'COMPLETED' || retryCount === MAX_RETRIES)) {
      setIsRequesting(false);
    }
    
    return result;
  };  

  useEffect(() => {
    setIsMounted(true);
    
    // Limpiamos el controller anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Creamos un nuevo controller
    abortControllerRef.current = new AbortController();

    if (dni) {
      // Reiniciamos estados
      setClientData(null);
      setClientStatus('');
      setClientAuthorization(null);
      setClientException(null);
      setActiveContent(null);
      setShowPurchaseFlow(false);
      setShowPaymentFlow(false);
      setShowHomonymModal(false);
      setShowNewClientModal(false);
      setShowBankSelectionModal(false);
      setCanRetryCredit(true);
      setRetryCountdown(0);
      setIsRequesting(false);

      // Iniciamos la búsqueda del perfil
      fetchClientProfile(dni);
    }

    return () => {
      setIsMounted(false);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [dni]);

  const clientPermissionsMap = {
    NEW: {
      canPurchase: true,
      canViewCurrentAccount: false,
      canRefinance: false,
      canViewProofDocuments: false,
      canMakePayments: process.env.REACT_APP_ALLOW_COLLECTION_MANAGEMENT === 'true' ? true : false,
      canEditProfile: false,
      canViewSales: true,
    },
    RENEWER: {
      canPurchase: true,
      canViewCurrentAccount: false,
      canRefinance: false,
      canViewProofDocuments: false,
      canMakePayments: process.env.REACT_APP_ALLOW_COLLECTION_MANAGEMENT === 'true' ? true : false,
      canEditProfile: false,
      canViewSales: true,
    },
    OLD: {
      canPurchase: false,
      canViewCurrentAccount: false,
      canRefinance: false,
      canViewProofDocuments: false,
      canMakePayments: process.env.REACT_APP_ALLOW_COLLECTION_MANAGEMENT === 'true' ? true : false,
      canEditProfile: false,
      canViewSales: true,
    },
    RESTRICTED: {
      canPurchase: false,
      canViewCurrentAccount: false,
      canRefinance: false,
      canViewProofDocuments: false,
      canMakePayments: process.env.REACT_APP_ALLOW_COLLECTION_MANAGEMENT === 'true' ? true : false,
      canEditProfile: false,
      canViewSales: true,
    },
    BLACKLISTED: {
      canPurchase: false,
      canViewCurrentAccount: false,
      canRefinance: false,
      canViewProofDocuments: false,
      canMakePayments: process.env.REACT_APP_ALLOW_COLLECTION_MANAGEMENT === 'true' ? true : false,
      canEditProfile: false,
      canViewSales: true,
    },
  };  

  const permissions = useMemo(() => {
    const basePermissions = clientPermissionsMap[clientStatus] || {
      canPurchase: false,
      canViewCurrentAccount: false,
      canRefinance: false,
      canViewProofDocuments: false,
      canMakePayments: process.env.REACT_APP_ALLOW_COLLECTION_MANAGEMENT === 'true' ? true : false,
      canEditProfile: false,
      canViewSales: false,
    };

    // Si hay una excepción aprobada, permitir compras incluso si el cliente está rechazado
    const hasApprovedException = clientException?.status === 'approved';
    
    if (hasApprovedException) {
      return {
        ...basePermissions,
        canPurchase: true,
      };
    }

    // Si el cliente está rechazado en la evaluación crediticia, no puede realizar compras
    // independientemente de su tipo de cliente
    if (clientData?.status === 'REJECTED') {
      return {
        ...basePermissions,
        canPurchase: false,
      };
    }

    // Si el cliente está en evaluación, tampoco puede realizar compras aún
    if (clientData?.status === 'PENDING_EVALUATION') {
      return {
        ...basePermissions,
        canPurchase: false,
      };
    }

    return basePermissions;
  }, [clientStatus, clientData?.status, clientException]);

  // useEffect para manejar la acción automática cuando el cliente se carga
  useEffect(() => {
    const action = getActionFromLocationState();
    if (action && clientData && !loading && clientData.status !== 'PENDING_EVALUATION') {
      // Esperar a que todo esté completamente cargado
      setTimeout(() => {
        handleAutomaticAction(action);
      }, 1000);
    }
  }, [clientData, loading, clientData?.status, permissions]);
  
  // Estado para el progreso y referencia al valor anterior
  const [progressValue, setProgressValue] = useState(0);
  const prevPercentageRef = useRef(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Calcular el porcentaje de saldo disponible
  const percentage = useMemo(() => {
    if (!clientData || clientData.total_limit === 0) return 0;
    const available = parseFloat(clientData.available_credit);
    const total = parseFloat(clientData.total_limit);
    return (available / total) * 100;
  }, [clientData]);

  // Animar el progreso del círculo
  useEffect(() => {
    if (loading || isLoading) {
      setProgressValue(0);
      setHasLoadedOnce(false);
      prevPercentageRef.current = 0;
      return;
    }
    // Si es la primera vez después del loading, animar desde 0
    if (!hasLoadedOnce) {
      setProgressValue(0);
      requestAnimationFrame(() => {
        setProgressValue(percentage);
        prevPercentageRef.current = percentage;
        setHasLoadedOnce(true);
      });
      return;
    }
    // Si cambia el porcentaje, animar desde el valor anterior
    if (prevPercentageRef.current !== percentage) {
      setProgressValue(prevPercentageRef.current);
      requestAnimationFrame(() => {
        setProgressValue(percentage);
        prevPercentageRef.current = percentage;
      });
    }
  }, [percentage, loading, isLoading, clientData?.dni]);
  
  const progressColor = useMemo(() => {
    if (percentage < 10) return '#ff0000'; // Red
    if (percentage < 30) return '#ffc107'; // Yellow
    return '#198722'; // Green
  }, [percentage]);
  
  const [displayedAvailableCredit, setDisplayedAvailableCredit] = useState(0);
  const prevCreditRef = useRef(0);

  // Animar el número del saldo disponible
  useEffect(() => {
    const target = parseFloat(clientData?.available_credit) || 0;
    const duration = 600; // ms
    const start = prevCreditRef.current;
    const startTime = performance.now();

    if (start === target) {
      setDisplayedAvailableCredit(target);
      return;
    }

    function animate(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const value = start + (target - start) * progress;
      setDisplayedAvailableCredit(value);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        prevCreditRef.current = target;
      }
    }

    requestAnimationFrame(animate);
  }, [clientData?.available_credit]);

  const renderProgressBar = () => {
    if (loading || isLoading) {
      return (
        <div className="text-center">
          <div className="progress-container">
            <CircularProgressbarWithChildren
              value={0}
              styles={buildStyles({
                pathColor: '#6c757d',
                trailColor: '#d6d6d6',
                pathTransitionDuration: 0.8,
              })}
            >
              <div style={{ textAlign: 'center' }}>
                <div className="spinner-border spinner-border-sm text-secondary mb-2" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="text-muted" style={{ fontSize: '12px', margin: '4px 0' }}>
                  Cargando perfil
                </p>
              </div>
            </CircularProgressbarWithChildren>
          </div>
          <div className="mt-4 w-100">
            <small className="text-muted d-block mb-2">
              <i className="bi bi-info-circle me-1"></i>
              Buscando información del cliente
            </small>
          </div>
        </div>
      );
    }
    if (clientData.status === 'PENDING_EVALUATION') {
      return (
        <div className="text-center">
          <div className="progress-container">
            <CircularProgressbarWithChildren
              value={100}
              styles={buildStyles({
                pathColor: '#6c757d',
                trailColor: '#d6d6d6',
                pathTransitionDuration: 0.8,
              })}
            >
              <div style={{ textAlign: 'center' }}>
                {isRequesting ? (
                  <div className="spinner-border spinner-border-sm text-secondary mb-2" role="status">
                    <span className="visually-hidden">Calculando...</span>
                  </div>
                ) : (
                  <i className="bi bi-clock-history text-secondary mb-2" style={{ fontSize: '1.2rem' }}></i>
                )}
                <p className="text-muted" style={{ fontSize: '12px', margin: '4px 0' }}>
                  Calculando crédito
                </p>
              </div>
            </CircularProgressbarWithChildren>
          </div>
          <div className="mt-4 w-100">
            <small className="text-muted d-block mb-2">
              <i className="bi bi-info-circle me-1"></i>
              Estamos evaluando tu perfil crediticio
            </small>
            {canRetryCredit && !isRequesting ? (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={handleManualCreditCheck}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Consultar crédito
              </button>
            ) : !canRetryCredit && !isRequesting ? (
              <small className="text-muted d-block">
                podrás consultarlo en {retryCountdown} segundos
              </small>
            ) : null}
          </div>
        </div>
      );
    } else if (clientData.status === 'REJECTED') {
      return (
        <div className="text-center">
          <div className="progress-container">
            <CircularProgressbarWithChildren
              value={100}
              styles={buildStyles({
                pathColor: '#dc3545',
                trailColor: '#d6d6d6',
                pathTransitionDuration: 0.8,
              })}
            >
              <div className='text-center'>
                <i className="bi bi-exclamation-triangle-fill text-danger mb-2" style={{ fontSize: '1.5rem' }}></i>
                <p className="text-danger mt-0" ><small>Sin Crédito</small></p>
              </div>
            </CircularProgressbarWithChildren>
          </div>
          <div className="mt-4 w-100">
            <p className="text-danger">
              <small>No hay línea crediticia para este perfil.</small>
            </p>
          </div>
        </div>
      );
    }
    // Estado normal: animar desde el valor anterior al nuevo
    return (
      <div style={{ width: '150px', margin: '0 auto' }}>
        <CircularProgressbarWithChildren
          value={progressValue}
          maxValue={100}
          styles={buildStyles({
            pathColor: progressColor,
            trailColor: '#d6d6d6',
            pathTransitionDuration: 0.8,
          })}
        >
          <div className="text-center">
            <p className={`m-0 ${progressText === 'Autorizado' ? 'text-success fw-bold' : 'text-muted'}`}>
              <small>{progressText}</small>
            </p>
            <strong className="text-black" style={{ fontSize: '18px' }}>
              {formatCurrency(displayedAvailableCredit)}
            </strong>
          </div>
        </CircularProgressbarWithChildren>
      </div>
    );
  };
  
  const startPurchaseFlow = () => {
    setShowPurchaseFlow(true);
    setActiveContent(null);
    setShowPaymentFlow(false);
  };

  const closePurchaseFlow = () => {
    setShowPurchaseFlow(false);
  };

  const startPaymentFlow = () => {
    setShowPurchaseFlow(false);
    setActiveContent(null);
    setShowPaymentFlow(true);
  };

  const closePaymentFlow = () => {
    setShowPaymentFlow(false);
  };

  const renderActiveContent = () => {
    if (showPurchaseFlow) {
      return (
        <PurchaseFlow clientStatus={clientStatus} maxAmount={clientData.available_credit} onClose={closePurchaseFlow} />
      );
    }

    if (showPaymentFlow) {
      return <PaymentFlow clientData={clientData} onClose={closePaymentFlow} />;
    }

    switch (activeContent) {
      case 'CurrentAccount':
        return <CurrentAccount />;
      case 'ClientProfile':
        return <ClientProfile />;
      case 'LoanRefinancing':
        return <LoanRefinancing />;
      case 'ProofDocuments':
        return <ProofDocuments />;
      case 'Sales':
        return <ClientSales clientId={clientData.id} />;
      default:
        return <p>Selecciona una opción para comenzar.</p>;
    }
  };

  const handleShareMiCuenta = async () => {
    if (!clientData?.my_account_url) return;

    try {
      const canvas = document.createElement('canvas');
      await QRCode.toCanvas(canvas, clientData.my_account_url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });

      const qrDataUrl = canvas.toDataURL();

      MySwal.fire({
        title: 'Portal Mi Cuenta',
        html: `
          <div class="text-center">
            <img src="${qrDataUrl}" class="mb-4" alt="QR Code" />
            <p class="mb-3">El cliente puede escanear este código QR para acceder a "Mi Cuenta", para realizar gestiones y consultas.</p>
            
            <div class="alert alert-warning d-flex align-items-center gap-2 mb-4 p-2" style="font-size: 0.9rem; max-width: 100%;">
              <div class="container-fluid">
                <div class="d-flex align-items-center gap-2">
                  <i class="bi bi-exclamation-triangle-fill"></i>
                  <div class="text-start">
                    Este link es único y personal para <strong>${clientData.firstName} ${clientData.lastName}</strong>. 
                    No debe ser compartido con otros clientes.
                  </div>
                </div>
              </div>
            </div>

            <hr />
            <div class="row g-3">
              <div class="col-6">
                <p class="text-muted small mb-2">Copia este enlace:</p>
                <div class="input-group">
                  <input 
                    type="text" 
                    value="${clientData.my_account_url}" 
                    class="form-control form-control-sm"
                    readonly
                  />
                  <button 
                    class="btn btn-outline-secondary copy-button"
                    type="button"
                    style="border: 1px solid #dce7f1 !important"
                    onclick="copyToClipboard('${clientData.my_account_url}', this)"
                  >
                    <i class="bi bi-clipboard"></i>
                  </button>
                </div>
              </div>
              <div class="col-6">
                <p class="text-muted small mb-2">O comparte con el cliente:</p>
                <a 
                  href="https://wa.me/?text=${encodeURIComponent(
                    `Hola ${clientData.firstName}, aquí puedes ver toda la información de tus préstamos y realizar gestiones: ${clientData.my_account_url}`
                  )}" 
                  target="_blank" 
                  class="btn btn-success w-100"
                  style="background-color: #25D366; border-color: #25D366"
                >
                  <i class="bi bi-whatsapp me-2"></i>
                  Enviar por WhatsApp
                </a>
              </div>
            </div>
          </div>
        `,
        showConfirmButton: false,
        showCloseButton: true,
        didOpen: () => {
          window.copyToClipboard = (text, button) => {
            navigator.clipboard.writeText(text).then(() => {
              const originalContent = button.innerHTML;
              button.innerHTML = '<i class="bi bi-check"></i>';
              button.disabled = true;
              setTimeout(() => {
                button.innerHTML = originalContent;
                button.disabled = false;
              }, 2000);
            });
          };
        }
      });
    } catch (error) {
      console.error('Error generando QR:', error);
    }
  };

  if (loading || showHomonymModal || showNewClientModal || showBankSelectionModal || !clientData) {
    return (
      <div className="page-content">
        {loading && (
          <div className="loading-overlay">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Cargando...</span>
            </div>
          </div>
        )}

        {showHomonymModal && (
          <HomonymModal
            homonyms={homonyms}
            onSelectClient={async (client) => {
              if (client.exists) {
                await handleExistingClient(client);
                setShowHomonymModal(false);
              } else {
                setSelectedNewClient(client);
                setShowHomonymModal(false);
                setShowNewClientModal(true);
              }
            }}
            onClose={() => {
              // setShowHomonymModal(false);
              window.location.href = '/';
            }}
          />
        )}

        {showNewClientModal && (
          <NewClientModal
            client={selectedNewClient}
            onConfirm={handleNewClientConfirm}
            onClose={() => {
              // setShowNewClientModal(false);
              window.location.href = '/';
            }}
            loading={loading}
          />
        )}

        {showBankSelectionModal && (
          <BankSelectionModal
            client={selectedNewClient}
            onConfirm={handleBankSelection}
            onClose={handleCloseBankSelection}
            loading={loading}
          />
        )}

        <div className="row">
          <div className="col-12 col-lg-3">
            <div className="card profile-card text-center">
              <div className="card-body">
                <Skeleton circle={true} height={100} width={100} className="mb-3" />
                <h3><Skeleton width={120} /></h3>
                <p className="text-muted"><Skeleton width={160} /></p>
                <Skeleton width={150} height={30} />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-9">
            <div className="row">
              <div className="col-md-6">
                <div className="card btn-card shadow mb-1" >
                  <div className="card-body text-center">
                    <Skeleton circle={true} height={50} width={50} className="mb-3" />
                    <h5 className="card-title mt-2"><Skeleton width={100} height={20} /></h5>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card btn-card shadow mb-1" >
                  <div className="card-body text-center">
                    <Skeleton circle={true} height={50} width={50} className="mb-3" />
                    <h5 className="card-title mt-2"><Skeleton width={100} height={20} /></h5>
                  </div>
                </div>
              </div>
            </div>
            <div className="card mt-2">
              <div className="card-body">
                <p><Skeleton height={40} /></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="row">
        <div className="col-12 col-lg-3">
          <div className="card profile-card">

            <div className="card-header position-relative m-0 p-4 pb-0">
              <button
                  className="btn btn-link btn-sm position-absolute top-0 end-0 text-primary me-1 mt-1"
                  onClick={handleShareMiCuenta}
                  title="Acceder a Mi Cuenta"
                  disabled={!clientData?.my_account_url}
                >
                <i className="bi bi-person-workspace"></i>
              </button>

              {/* Información básica */}
              <div className="row">
                <div className="d-flex align-items-center me-3">
                  <div className="avatar avatar-xl me-3">
                    <img src={clientData.gender === 'F' ? '/assets/compiled/jpg/female-profile.jpg' : '/assets/compiled/jpg/male-profile.jpg'}  alt="Avatar"  className="rounded-circle" />
                  </div>
                  <div>
                    <h4 className="mb-0">
                      {clientData?.lastName?.length > 11 ? `${clientData?.lastName?.substring(0,11)}...` : clientData?.lastName}<br></br> <small> {clientData?.firstName?.length > 14 ? `${clientData?.firstName?.substring(0,14)}...` : clientData?.firstName}</small>
                    </h4>
                    <small className="text-muted">DNI: { formatDNI(clientData?.dni)}</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="divider">
                <div className="divider-text small">Estado Crediticio</div>
              </div>

              <div className="row text-center">
                <div className="col-12">
                  <div className="mt-2 mb-2" style={{ margin: '0 auto', position: 'relative', }} >
                    {renderProgressBar()}
                  </div>
                  {clientData.status !== 'PENDING_EVALUATION' && clientData.status !== 'REJECTED' && (
                    <div className="mt-4 w-100">
                      <small className="text-muted d-block mb-2">
                        Crédito Total: ${formatNumber(clientData.total_limit)}
                      </small>
                    </div>
                  )}
                  {clientAuthorization && ['pending', 'rejected', 'cancelled'].includes(clientAuthorization.status) && (
                    <>
                      <div className={`alert ${clientAuthorization.status === 'pending' ? 'alert-warning' : 'alert-danger'} mb-0 p-1`}>
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <span>
                          {clientAuthorization.status === 'pending' && "Autorización pendiente."}
                          {['rejected', 'cancelled'].includes(clientAuthorization.status) && `Última autorización fue ${clientAuthorization.status_label}.`}
                        </span>
                      </div>

                      {clientAuthorization && clientAuthorization.status === 'pending' && (
                        <button
                          className="btn btn-sm btn-outline-secondary mt-2"
                          onClick={handleAuthorizationCheck}
                        >
                          <i className="bi bi-arrow-clockwise me-1"></i>Consultar estado
                        </button>
                      )}
                    </>
                  )}
                  {clientException && ['pending', 'rejected', 'cancelled'].includes(clientException.status) && (
                    <>
                      <div className={`alert ${clientException.status === 'pending' ? 'alert-warning' : 'alert-danger'} mb-0 p-1`}>
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <span>
                          {clientException.status === 'pending' && "Excepción pendiente."}
                          {['rejected', 'cancelled'].includes(clientException.status) && `Última excepción fue ${clientException.status_label}.`}
                        </span>
                      </div>

                      {clientException && clientException.status === 'pending' && (
                        <button
                          className="btn btn-sm btn-outline-secondary mt-2"
                          onClick={handleAuthorizationCheck}
                        >
                          <i className="bi bi-arrow-clockwise me-1"></i>Consultar estado
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="divider">
                <div className="divider-text small">Información Adicional</div>
              </div>

              {/* Información Adicional */}
              <div className="row g-3 mt-2">

                {/* Situación Laboral */}
                <div className="col-12 mt-2">
                  {isRequesting ? (
                    <Skeleton className="card mb-0 border-0" height={60} />
                  ) : (
                    <div className="card bg-light-info mb-0">
                      <div className="card-body p-2 ps-3">
                        <div className="d-flex align-items-center">
                          <div className="avatar avatar-sm bg-info me-2">
                            <i className="bi bi-briefcase"></i>
                          </div>
                          <div>
                            <small className="text-muted d-block">Situación Laboral</small>
                            <span className="fw-bold">{clientData?.employment_status?.name || 'N/A'}</span>
                            {/* {clientData?.employment_status?.description && (
                              <small className="text-muted d-block">
                                {clientData.employment_status.description}
                              </small>
                            )} */}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tipo de Cliente */}
                <div className="col-6 mt-2">
                  {isRequesting ? (
                    <Skeleton className="card mb-0 m-0 p-0 border-0" height={60} />
                  ) : (
                    <div className="card bg-light-secondary mb-0">
                      <div className="card-body p-2 ps-3">
                        <div className="d-flex align-items-center">
                          <div className="avatar avatar-sm bg-secondary me-2">
                            <i className="bi bi-person"></i>
                          </div>
                          <div>
                            <small className="text-muted d-block">Cliente</small>
                            <span className="fw-bold">{clientData?.client_type?.label || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Nivel de Riesgo */}
                <div className="col-6 mt-2">
                  {isRequesting ? (
                    <Skeleton className="card mb-0 m-0 p-0 border-0" height={60} />
                  ) : (
                    <div className="card bg-light-warning mb-0">
                      <div className="card-body p-2 ps-3">
                        <div className="d-flex align-items-center text-center">
                          <div className="avatar avatar-sm bg-warning me-2">
                            <i className="bi bi-shield-check"></i>
                          </div>
                          <div>
                            <small className="text-muted d-block">Riesgo</small>
                            <span className="fw-bold">{clientData?.risk_level || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Navegación */}
              <div className="divider">
                <div className="divider-text small">Navegación</div>
              </div>

              <div className="d-grid gap-1">
                <button
                  className={`btn ${
                    showPurchaseFlow 
                      ? 'btn-success' 
                      : 'btn-outline-success'
                  } w-100 text-center py-4 position-relative main-action-button ${
                    clientData?.status === 'PENDING_EVALUATION' || 
                    clientData?.status === 'REJECTED' || 
                    !permissions.canPurchase 
                      ? 'disabled' 
                      : ''
                  }`}
                  id="purchase"
                  disabled={
                    clientData?.status === 'PENDING_EVALUATION' || 
                    clientData?.status === 'REJECTED' || 
                    !permissions.canPurchase
                  }
                  style={{ 
                    cursor: clientData.status === 'PENDING_EVALUATION' || 
                            clientData.status === 'REJECTED' 
                              ? 'not-allowed' 
                              : 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: showPurchaseFlow 
                      ? '0 0 0 0.25rem rgba(25, 135, 84, 0.25)' 
                      : 'none'
                  }} 
                  title={
                    clientData.status === 'PENDING_EVALUATION' 
                      ? "El límite de crédito está siendo calculado" 
                      : clientData.status === 'REJECTED'
                      ? "Cliente sin línea crediticia disponible"
                      : !permissions.canPurchase
                      ? "No tienes permisos para realizar ventas"
                      : "Inicia una nueva venta para el cliente"
                  }  
                  onClick={
                    clientData?.status !== 'PENDING_EVALUATION' && 
                    clientData?.status !== 'REJECTED' && 
                    permissions.canPurchase 
                      ? startPurchaseFlow 
                      : undefined
                  }
                >
                  <div className="d-flex flex-column align-items-center justify-content-center w-100">
                    <h3>
                      <i className={`bi bi-cart ${
                        showPurchaseFlow ? 'text-white' : 'text-success'
                      } mb-2`}></i>
                    </h3>
                    <span className="d-block">Nueva Venta</span>
                  </div>
                </button>

                {/* Botón de Cobrar Cuotas - Solo se muestra si está habilitado */}
                {permissions.canMakePayments && (
                  <button
                    className={`btn ${
                      showPaymentFlow 
                        ? 'btn-primary' 
                        : 'btn-outline-primary'
                    } w-100 text-center py-4 position-relative main-action-button`}
                    id="payment"
                    style={{ 
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: showPaymentFlow 
                        ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' 
                        : 'none'
                    }}
                    title="Realiza pagos en nombre del cliente"
                    onClick={startPaymentFlow}
                  >
                    <div className="d-flex flex-column align-items-center justify-content-center w-100">
                      <h3>
                        <i className={`bi bi-credit-card ${
                          showPaymentFlow ? 'text-white' : 'text-primary'
                        } mb-2`}></i>
                      </h3>
                      <span className="d-block">Cobrar Cuotas</span>
                    </div>
                  </button>
                )}

                <UnifiedAuthorizationModal
                  show={showUnifiedAuthorizationModal}
                  onClose={() => setShowUnifiedAuthorizationModal(false)}
                  onRequestAuthorization={handleRequestAuthorization}
                  onRequestException={handleRequestOverrideException}
                  customerDni={clientData?.dni}
                  reason="manual_request"
                />

                {(process.env.REACT_APP_ENABLE_AUTHORIZATIONS === 'true' || 
                  process.env.REACT_APP_ENABLE_OVERRIDE_EXCEPTIONS === 'true') && (
                  <button
                    className={`btn btn-light-primary w-100 text-start py-2 ${
                      (clientAuthorization && clientAuthorization.status === 'pending') ||
                      (clientException && clientException.status === 'pending')
                        ? 'border-0' 
                        : ''
                    }`}
                    onClick={() => setShowUnifiedAuthorizationModal(true)}
                    disabled={
                      (clientAuthorization && clientAuthorization.status === 'pending') ||
                      (clientException && clientException.status === 'pending')
                    }
                  >
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faCirclePlus} className="me-2" />
                      <small className="d-block">Solicitar Autorización</small>
                    </div>
                  </button>
                )}

                

                {permissions.canViewSales && (
                  <button
                    className={`btn btn-light-primary w-100 text-start py-2 ${
                      activeContent === 'Sales' ? 'btn-outline-primary' : ''
                    }`}
                    onClick={() => {
                      setActiveContent('Sales');
                      setShowPurchaseFlow(false);
                      setShowPaymentFlow(false);
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faTags} className="me-2" />
                        <small className="d-block">Consultar Ventas</small>
                      </div>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                  </button>
                )}
                
                {permissions.canViewProofDocuments && (
                  <button
                    className={`btn btn-light-primary w-100 text-start py-2 ${
                      activeContent === 'ProofDocuments' ? 'btn-outline-primary' : ''
                    }`}
                    onClick={() => {
                      setActiveContent('ProofDocuments');
                      setShowPurchaseFlow(false);
                      setShowPaymentFlow(false);
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faFolderOpen} className="me-2" />
                        <small className="d-block">Datos Personales</small>
                      </div>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                  </button>
                )}
                
                {permissions.canRefinance && (
                  <button
                    className={`btn btn-light-primary w-100 text-start py-2 ${
                      activeContent === 'LoanRefinancing' ? 'btn-outline-primary' : ''
                    }`}
                    onClick={() => {
                      setActiveContent('LoanRefinancing');
                      setShowPurchaseFlow(false);
                      setShowPaymentFlow(false);
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                        <small className="d-block">Refinanciaciones</small>
                      </div>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                  </button>
                )}
                
                {permissions.canEditProfile && (
                  <button
                    className={`btn btn-light-primary w-100 text-start py-2 ${
                      activeContent === 'ClientProfile' ? 'btn-outline-primary' : ''
                    }`}
                    onClick={() => {
                      setActiveContent('ClientProfile');
                      setShowPurchaseFlow(false);
                      setShowPaymentFlow(false);
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faFileLines} className="me-2" />
                        <small className="d-block">Constancias</small>
                      </div>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                  </button>
                )}
              </div>

            </div>
          </div>
        </div>
        <div className="col-12 col-lg-9">
          {false && permissions.canMakePayments && (
            <div className="row">
              <div className="col-md-6">
                <div 
                  className={`card btn-card shadow mb-1 main-action ${
                    showPurchaseFlow ? 'selected' : ''
                  } ${
                    clientData?.status === 'PENDING_EVALUATION' || clientData?.status === 'REJECTED' || !permissions.canPurchase ? 'disabled' : ''
                  }`}
                  id="purchase"
                  style={{ 
                    cursor: clientData.status === 'PENDING_EVALUATION' || clientData.status === 'REJECTED' ? 'not-allowed' : 'pointer',
                    opacity: clientData.status === 'PENDING_EVALUATION' || clientData.status === 'REJECTED' ? 0.6 : 1,
                    boxShadow: showPurchaseFlow 
                      ? '0 0 0 0.25rem rgba(25, 135, 84, 0.25)' 
                      : 'none'
                  }} 
                  title={
                    clientData.status === 'PENDING_EVALUATION' 
                      ? "El límite de crédito está siendo calculado" 
                      : "Inicia una nueva venta para el cliente"
                  }
                  onClick={clientData?.status !== 'PENDING_EVALUATION' && clientData?.status !== 'REJECTED' && permissions.canPurchase ? startPurchaseFlow : undefined}
                >
                  <div className="card-body text-center">
                    <i className={`bi bi-cart ${
                      showPurchaseFlow ? 'text-success' : 'text-secondary'
                    } display-5`}></i>
                    <h5 className={`card-title mt-2 ${
                      showPurchaseFlow ? 'text-success' : 'text-secondary'
                    }`}>Nueva Venta</h5>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div
                  className={`card btn-card shadow mb-1 main-action ${
                    showPaymentFlow ? 'selected' : ''
                  } ${
                    !permissions.canMakePayments ? 'disabled' : ''
                  }`}
                  id="payment"
                  style={{ 
                    cursor: !permissions.canMakePayments ? 'not-allowed' : 'pointer', 
                    opacity: !permissions.canMakePayments ? 0.6 : 1,
                    boxShadow: showPaymentFlow 
                      ? '0 0 0 0.25rem rgba(25, 135, 84, 0.25)' 
                      : 'none'
                  }}
                  title="Realiza pagos en nombre del cliente."
                  onClick={permissions.canMakePayments ? startPaymentFlow : undefined}
                >
                  <div className="card-body text-center">
                    <i className={`bi bi-credit-card ${
                      showPaymentFlow ? 'text-success' : 'text-secondary'
                    } display-5`}></i>
                    <h5 className={`card-title mt-2 ${
                      showPaymentFlow ? 'text-success' : 'text-secondary'
                    }`}>Cobrar Cuotas</h5>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="card">
            <div className="card-body" id="content-area">
              {renderActiveContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
